import {
  type IssuedInvitation,
  type PlatformCapability,
  type TenantDetail,
  type TenantLifecycleAction,
  type TenantOwner,
} from '@avrahami/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  keys,
  platformApi,
  tenantContactQuery,
  tenantDesignQuery,
  tenantQuery,
} from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { AuditTable } from '../components/AuditTable';
import { ManageStore } from '../components/ManageStore';
import { ProvisioningCard } from '../components/ProvisioningCard';
import { CertificateDate, DomainStatusBadge, TenantStatusBadge } from '../components/status';
import { TenantServiceCard } from '../components/ServiceTickets';
import { TemplatePreview } from '../components/StoreTemplates';
import { TenantAiCard } from '../components/TenantAiCard';
import { TenantExclusionsCard } from '../components/TenantExclusionsCard';
import { StoreCustomerCard } from '../components/StoreCustomerCard';
import { he } from '../i18n/he';
import { errorMessage, ownerInvitationLink } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyField,
  formatDate,
  formatDateTime,
  Loading,
  PageHeader,
  TextField,
  returnToPrevious,
} from '@avrahami/ui';

export function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/app/tenants/$tenantId' });
  const detail = useQuery(tenantQuery(tenantId));
  const session = useQuery(sessionQuery);
  const can = (capability: PlatformCapability) =>
    session.data !== undefined && hasCapability(session.data, capability);

  if (detail.isPending) return <Loading />;
  if (detail.isError) return <Alert>{errorMessage(detail.error)}</Alert>;
  const { tenant, domains, owners } = detail.data;

  return (
    <>
      <Link
        to="/tenants"
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.tenant.back}
      </Link>
      <PageHeader
        title={tenant.displayName}
        actions={
          <div className="flex flex-wrap items-start justify-end gap-3">
            {can('platform.tenants.manage') &&
            (tenant.status === 'ACTIVE' || tenant.status === 'SUSPENDED') ? (
              <ManageStore tenantId={tenant.id} />
            ) : null}
            {can('platform.tenants.suspend') ? <Lifecycle detail={detail.data} /> : null}
          </div>
        }
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={he.tenant.summary}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-slate-500">{he.tenants.status}</dt>
            <dd>
              <TenantStatusBadge status={tenant.status} />
            </dd>
            <dt className="text-slate-500">{he.tenants.slug}</dt>
            <dd className="ltr">{tenant.slug}</dd>
            {tenant.kind === 'CUSTOMER' ? (
              <>
                <dt className="text-slate-500">{he.tenant.customerNumber}</dt>
                <dd className="ltr font-mono">
                  {detail.data.customerNumber ?? he.tenant.customerNumberNone}
                </dd>
              </>
            ) : null}
            <dt className="text-slate-500">{he.tenants.kind}</dt>
            <dd>{he.tenantKind[tenant.kind]}</dd>
            <dt className="text-slate-500">{he.tenants.created}</dt>
            <dd>{formatDate(tenant.createdAt)}</dd>
            <dt className="text-slate-500">{he.tenant.version}</dt>
            <dd>{tenant.version}</dd>
          </dl>
        </Card>
        {tenant.kind === 'CUSTOMER' ? (
          <StoreCustomerCard detail={detail.data} canChange={can('platform.tenants.create')} />
        ) : null}
        <Card title={he.tenant.domains}>
          <ul className="flex flex-col gap-2 text-sm">
            {domains.map((domain) => (
              <li
                key={domain.id}
                className="flex flex-col gap-1"
                data-testid={`domain-row-${domain.hostname}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="ltr font-mono">{domain.url.replace(/^https:\/\//, '')}</span>
                  <DomainStatusBadge domain={domain} />
                  {domain.isPrimary ? <Badge tone="blue">{he.tenant.primary}</Badge> : null}
                </div>
                {domain.reason === null ? null : (
                  <p
                    className="text-xs text-slate-700"
                    data-testid="domain-reason"
                    data-reason={domain.reason}
                  >
                    {he.domains.reasons[domain.reason]}
                  </p>
                )}
                {domain.tlsValidUntil === null ? null : (
                  <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {he.domains.certificateUntil}:
                    <CertificateDate validUntil={domain.tlsValidUntil} now={detail.dataUpdatedAt} />
                  </p>
                )}
              </li>
            ))}
          </ul>
          <Link
            to="/tenants/$tenantId/domains"
            params={{ tenantId: tenant.id }}
            data-testid="tenant-domains-link"
            className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline"
          >
            {he.storeDomains.manage}
          </Link>
        </Card>
        <Card title={he.tenant.owners}>
          <ul className="flex flex-col gap-4">
            {owners.map((owner) => (
              <OwnerRow
                key={owner.userId}
                tenantId={tenant.id}
                owner={owner}
                canInvite={can('platform.tenants.create')}
                canSetPassword={can('platform.tenants.manage')}
              />
            ))}
          </ul>
        </Card>
        <ContactCard tenantId={tenant.id} />
        <DesignCard tenantId={tenant.id} />
        <TenantServiceCard tenantId={tenant.id} canManage={can('platform.support.manage')} />
      </div>
      <div className="mt-6">
        <ProvisioningCard
          tenantId={tenant.id}
          canRetry={can('platform.tenants.create')}
          showCopyProgress={can('platform.ai.view')}
          canStopCopy={can('platform.ai.manage')}
        />
      </div>
      {can('platform.ai.view') ? (
        <div className="mt-6">
          <TenantAiCard tenantId={tenant.id} canManage={can('platform.ai.manage')} />
        </div>
      ) : null}
      {tenant.kind === 'CUSTOMER' ? (
        <div className="mt-6">
          <TenantExclusionsCard
            tenantId={tenant.id}
            canChange={can('platform.tenants.create')}
            canPick={can('platform.catalog.read')}
          />
        </div>
      ) : null}
      {can('platform.audit.read') ? (
        <div className="mt-6">
          <Card title={he.tenant.history}>
            <AuditTable tenantId={tenant.id} />
          </Card>
        </div>
      ) : null}
    </>
  );
}

/** The store's WhatsApp number and mail account at a glance; they are set on a screen of their own. */
function ContactCard({ tenantId }: { tenantId: string }) {
  const contact = useQuery(tenantContactQuery(tenantId));
  return (
    <Card title={he.storeContact.title}>
      {contact.isPending ? <Loading /> : null}
      {contact.isError ? <Alert>{errorMessage(contact.error)}</Alert> : null}
      {contact.data === undefined ? null : (
        <dl
          className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm"
          data-testid="tenant-contact"
        >
          <dt className="text-slate-500">{he.storeContact.whatsapp}</dt>
          <dd data-testid="tenant-contact-whatsapp">
            {contact.data.whatsapp !== null ? (
              <bdi dir="ltr">{contact.data.whatsapp}</bdi>
            ) : contact.data.phone === null ? (
              he.storeContact.whatsappEmpty
            ) : (
              <>
                {he.storeContact.whatsappByPhone} <bdi dir="ltr">{contact.data.phone}</bdi>
              </>
            )}
          </dd>
          <dt className="text-slate-500">{he.storeContact.mail}</dt>
          <dd data-testid="tenant-contact-mail">
            {contact.data.mail.gmailAddress === null ? (
              he.storeContact.mailNone
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <bdi dir="ltr">{contact.data.mail.gmailAddress}</bdi>
                {contact.data.mail.failedAt === null ? null : (
                  <Badge tone="amber">{he.storeContact.mailFailedShort}</Badge>
                )}
              </div>
            )}
          </dd>
        </dl>
      )}
      <Link
        to="/tenants/$tenantId/contact"
        params={{ tenantId }}
        data-testid="tenant-contact-link"
        className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline"
      >
        {he.storeContact.manage}
      </Link>
    </Card>
  );
}

/** The store's template and colours at a glance; they are set on a screen of their own. */
function DesignCard({ tenantId }: { tenantId: string }) {
  const design = useQuery(tenantDesignQuery(tenantId));
  return (
    <Card title={he.storeDesign.title}>
      {design.isPending ? <Loading /> : null}
      {design.isError ? <Alert>{errorMessage(design.error)}</Alert> : null}
      {design.data === undefined ? null : (
        <div className="flex items-start gap-4" data-testid="tenant-design">
          <div className="w-36 shrink-0">
            <TemplatePreview template={design.data.template} colors={design.data.colors} />
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-slate-500">{he.storeDesign.template}</dt>
            <dd data-testid="tenant-design-template">
              {he.storeDesign.templates[design.data.template].name}
            </dd>
            <dt className="text-slate-500">{he.storeDesign.colors}</dt>
            <dd className="flex gap-1">
              {(['brand', 'accent', 'ink'] as const).map((key) => (
                <span
                  key={key}
                  title={`${he.storeDesign[key]} ${design.data.colors[key]}`}
                  className="inline-block h-5 w-5 rounded ring-1 ring-slate-300"
                  style={{ backgroundColor: design.data.colors[key] }}
                />
              ))}
            </dd>
            <dt className="text-slate-500">{he.storeDesign.typeface}</dt>
            <dd>{he.storeDesign.typefaces[design.data.typeface]}</dd>
          </dl>
        </div>
      )}
      <Link
        to="/tenants/$tenantId/design"
        params={{ tenantId }}
        data-testid="tenant-design-link"
        className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline"
      >
        {he.storeDesign.manage}
      </Link>
    </Card>
  );
}

function OwnerRow({
  tenantId,
  owner,
  canInvite,
  canSetPassword,
}: {
  tenantId: string;
  owner: TenantOwner;
  canInvite: boolean;
  canSetPassword: boolean;
}) {
  const queryClient = useQueryClient();
  const [issued, setIssued] = useState<IssuedInvitation>();
  const invite = useMutation({
    mutationFn: () => platformApi.inviteOwner(tenantId, owner.userId),
    onSuccess: async (result) => {
      setIssued(result);
      await queryClient.invalidateQueries({ queryKey: keys.tenant(tenantId) });
    },
  });
  return (
    <li className="flex flex-col gap-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <strong>{owner.displayName}</strong>
        <span className="ltr text-slate-600">{owner.email}</span>
        {owner.needsPasswordSetup ? (
          <Badge tone="amber">{he.tenant.needsPassword}</Badge>
        ) : (
          <Badge tone="green">{he.tenant.passwordSet}</Badge>
        )}
      </div>
      {owner.needsPasswordSetup ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          {owner.invitationExpiresAt === null
            ? he.tenant.noInvitation
            : `${he.tenant.invitationUntil} ${formatDateTime(owner.invitationExpiresAt)}`}
          {canInvite ? (
            <Button
              tone="secondary"
              busy={invite.isPending}
              onClick={() => {
                invite.mutate();
              }}
            >
              {he.tenant.newInvitation}
            </Button>
          ) : null}
        </div>
      ) : null}
      {canSetPassword ? <OwnerPassword tenantId={tenantId} userId={owner.userId} /> : null}
      {invite.isError ? <Alert>{errorMessage(invite.error)}</Alert> : null}
      {issued === undefined ? null : (
        <>
          <p className="text-xs text-slate-600">{he.tenant.newInvitationBody}</p>
          <CopyField
            label={he.tenant.newInvitation}
            value={ownerInvitationLink(issued.token)}
            testId="invitation-link"
          />
        </>
      )}
    </li>
  );
}

/**
 * Sets a new password for a store owner who cannot get in (adr/0035, 7). The password is the
 * person's, so it changes in every store of theirs, and their open sessions end.
 */
function OwnerPassword({ tenantId, userId }: { tenantId: string; userId: string }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () => platformApi.setOwnerPassword(tenantId, userId, password),
    onSuccess: async () => {
      setOpen(false);
      setPassword('');
      setConfirm('');
      // The owner's badge turns to "set a password" and an open invitation is gone.
      await queryClient.invalidateQueries({ queryKey: keys.tenant(tenantId) });
    },
  });
  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          tone="secondary"
          onClick={() => {
            save.reset();
            setOpen(true);
          }}
        >
          {he.tenant.setPassword}
        </Button>
        {save.isSuccess ? (
          <span role="status" className="text-xs text-green-800">
            {he.tenant.passwordSaved}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <form
      className="flex max-w-sm flex-col gap-3 rounded-md border border-slate-200 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (password !== confirm) {
          setMismatch(true);
          return;
        }
        setMismatch(false);
        save.mutate();
      }}
    >
      <p className="text-xs text-slate-600">{he.tenant.setPasswordHint}</p>
      <TextField
        label={he.tenant.newPassword}
        name="owner-new-password"
        type="password"
        autoComplete="new-password"
        minLength={10}
        required
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
      <TextField
        label={he.tenant.confirmPassword}
        name="owner-confirm-password"
        type="password"
        autoComplete="new-password"
        minLength={10}
        required
        value={confirm}
        error={mismatch ? he.tenant.passwordMismatch : undefined}
        onChange={(event) => {
          setConfirm(event.target.value);
        }}
      />
      {save.isError ? <Alert>{errorMessage(save.error)}</Alert> : null}
      <div className="flex gap-2">
        <Button type="submit" busy={save.isPending}>
          {he.tenant.savePassword}
        </Button>
        <Button
          tone="secondary"
          onClick={() => {
            setOpen(false);
          }}
        >
          {he.tenant.cancelPassword}
        </Button>
      </div>
    </form>
  );
}

function Lifecycle({ detail }: { detail: TenantDetail }) {
  const queryClient = useQueryClient();
  const { tenant } = detail;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const action: TenantLifecycleAction | null =
    tenant.status === 'ACTIVE' ? 'suspend' : tenant.status === 'SUSPENDED' ? 'reactivate' : null;
  const change = useMutation({
    mutationFn: (next: TenantLifecycleAction) =>
      platformApi.changeLifecycle(tenant.id, next, reason),
    onSuccess: async () => {
      setOpen(false);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: keys.tenants });
      await queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  if (tenant.kind === 'MASTER') {
    return <p className="text-xs text-slate-500">{he.tenant.masterNote}</p>;
  }
  if (action === null) return null;
  const suspend = action === 'suspend';
  return (
    <>
      <Button
        tone={suspend ? 'danger' : 'primary'}
        onClick={() => {
          change.reset();
          setOpen(true);
        }}
      >
        {suspend ? he.tenant.suspend : he.tenant.reactivate}
      </Button>
      <ConfirmDialog
        open={open}
        title={suspend ? he.tenant.suspendTitle : he.tenant.reactivateTitle}
        confirmLabel={suspend ? he.tenant.suspend : he.tenant.reactivate}
        tone={suspend ? 'danger' : 'primary'}
        busy={change.isPending}
        onConfirm={() => {
          change.mutate(action);
        }}
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <p>{suspend ? he.tenant.suspendBody : he.tenant.reactivateBody}</p>
          <TextField
            label={he.tenant.reason}
            name="reason"
            maxLength={500}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          {change.isError ? <Alert>{errorMessage(change.error)}</Alert> : null}
        </div>
      </ConfirmDialog>
    </>
  );
}
